import React, { useState, useEffect, useCallback } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

export default function WorkshopPhotoCarousel({
  photos = [],
  height = 320,
  autoPlay = true,
  interval = 3000,
  objectFit = 'contain',
  borderRadius = 2,
}) {
  const [index, setIndex] = useState(0);
  const [animate, setAnimate] = useState(true);

  const go = useCallback((next) => {
    setAnimate(true);
    setIndex(next);
  }, []);

  const prev = useCallback(() => go((index - 1 + photos.length) % photos.length), [go, index, photos.length]);
  const next = useCallback(() => go((index + 1) % photos.length), [go, index, photos.length]);

  useEffect(() => {
    if (!autoPlay || photos.length <= 1) return;
    const t = setInterval(next, interval);
    return () => clearInterval(t);
  }, [autoPlay, photos.length, interval, next]);

  if (!photos.length) return null;

  return (
    <Box sx={{ position: 'relative', height, overflow: 'hidden', borderRadius, bgcolor: '#111' }}>
      {/* Sliding strip */}
      <Box sx={{
        display: 'flex',
        width: `${photos.length * 100}%`,
        height: '100%',
        transform: `translateX(-${index * (100 / photos.length)}%)`,
        transition: animate ? 'transform 0.45s cubic-bezier(0.25, 0.46, 0.45, 0.94)' : 'none',
        willChange: 'transform',
      }}>
        {photos.map((photo, i) => (
          <Box
            key={i}
            component="img"
            src={photo.url}
            alt={photo.name || ''}
            sx={{
              width: `${100 / photos.length}%`,
              height: '100%',
              objectFit,
              flexShrink: 0,
              display: 'block',
            }}
          />
        ))}
      </Box>

      {photos.length > 1 && (
        <>
          <IconButton onClick={prev} size="small" sx={{
            position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 2,
            bgcolor: 'rgba(0,0,0,0.45)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
          }}>
            <ChevronLeftIcon />
          </IconButton>
          <IconButton onClick={next} size="small" sx={{
            position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 2,
            bgcolor: 'rgba(0,0,0,0.45)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
          }}>
            <ChevronRightIcon />
          </IconButton>

          <Box sx={{
            position: 'absolute', bottom: 10, left: 0, right: 0, zIndex: 2,
            display: 'flex', justifyContent: 'center', gap: 0.75,
          }}>
            {photos.map((_, i) => (
              <Box key={i} onClick={() => go(i)} sx={{
                width: i === index ? 20 : 8, height: 8, borderRadius: 4,
                bgcolor: i === index ? '#fff' : 'rgba(255,255,255,0.45)',
                cursor: 'pointer', transition: 'all 0.2s',
              }} />
            ))}
          </Box>

          <Box sx={{
            position: 'absolute', top: 10, right: 10, zIndex: 2,
            bgcolor: 'rgba(0,0,0,0.5)', borderRadius: 2, px: 1, py: 0.25,
          }}>
            <Typography sx={{ fontSize: '0.72rem', color: '#fff', fontWeight: 600 }}>
              {index + 1} / {photos.length}
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
}
